import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Loader } from '@piggytechng/component-library';
import { IoCopyOutline } from 'react-icons/io5';
import Header from '../../../../../../components/Header/Header';
import NoData from '../../../../../../components/NoData/NoData';
import Pagination from '../../../../../../components/Pagination/Pagination';
import { copyToClipboard, formatToNaira } from '../../../../../../helpers/utils';
import { IpoOfferDetailsLocationState } from '../../../../../../types';
import useIpo from '../../../../../../hooks/useIpo';
import IpoSubscriptionsTable from '../IpoSubscriptionsTable';
import IpoSubscriptionDetails from '../IpoSubscriptionDetails';
import '../../../WalletsTab/Wallets/Investify/SingleInvestment/index.scss';
import '../IpoTab.scss';

const SingleIpoOffer = () => {
  const { offerId } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const passedOffer = (location.state as IpoOfferDetailsLocationState | null)?.offer;

  const { subscriptions, isLoading, subscriptionDetails, fetchingDetails, subscriptionsHasNextPage, offerSummary } =
    useIpo({
      page,
      limit,
      offerId,
      subscriptionId: selectedId,
      fetchOfferSummary: true,
    });

  const selectedRow = subscriptions.find((subscription) => subscription.id === selectedId) ?? null;
  const summary = useMemo(() => {
    if (offerSummary) return offerSummary;
    if (passedOffer?.offerId === offerId) return passedOffer;
    return null;
  }, [offerId, offerSummary, passedOffer]);

  return (
    <div className="single-investment-page single-ipo-investment-page ipo-tab">
      <Header name="IPO Investment Details" backButton />

      <div className="content">
        <div className="investment-header">
          <div className="left-section">
            <h1>{summary?.offerName || 'IPO Investment'}</h1>
            <div className="id-container">
              <span className="flex--2 cont">
                <p>Offer ID: {offerId}</p>
                <IoCopyOutline
                  size={18}
                  className="pointer"
                  onClick={() => copyToClipboard('Offer ID', offerId || '')}
                />
              </span>
            </div>
          </div>
        </div>

        {summary && (
          <div className="investment-data">
            <div className="data-box">
              <p>Total Amount</p>
              <h3>{formatToNaira(summary.totalAmount)}</h3>
            </div>
            <div className="data-box">
              <p>Total Units</p>
              <h3>{summary.totalUnits.toLocaleString()}</h3>
            </div>
            <div className="data-box">
              <p>Subscriptions</p>
              <h3>{summary.subscriptionCount.toLocaleString()}</h3>
            </div>
          </div>
        )}

        <h1 className="investment-offers-heading">Subscriptions</h1>

        {isLoading ? (
          <div className="ipo-tab__loader">
            <Loader color="#0F60D6" />
          </div>
        ) : subscriptions.length > 0 ? (
          <>
            <IpoSubscriptionsTable rows={subscriptions} onRowClick={(row) => setSelectedId(row.id)} />
            <Pagination
              currentPage={page}
              hasNextPage={subscriptionsHasNextPage}
              hasPrevPage={page > 1}
              onNextPage={() => setPage((current) => current + 1)}
              onPrevPage={() => setPage((current) => Math.max(current - 1, 1))}
              limit={limit}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
          </>
        ) : (
          <NoData message="No subscriptions at the moment." />
        )}
      </div>

      <IpoSubscriptionDetails
        show={selectedId != null}
        onClose={() => setSelectedId(null)}
        data={subscriptionDetails ?? selectedRow}
        isLoading={Boolean(selectedId) && fetchingDetails && !subscriptionDetails}
      />
    </div>
  );
};

export default SingleIpoOffer;
